// VtOutOfView V1.2
// disables view-transition-name on elements outside the viewport

// init:
// vtOutOfView = new VtOutOfView();
// vtOutOfView.init();

// usage:
// set data-vt-name="your-name" and style="view-transition-name: your-name;" in templates
// view-transition-name is needed in the template, because only setting it later breaks the vt
// call vtOutOfView.init() once so it listens to scroll/resize and checks automatically
// optionally call vtOutOfView.refreshElements() when content changes

// required properties:
// none

// possible properties:
// selector = '[data-vt-name]': selector used to gather tracked VT elements
// listenToScroll = true: run checks on window scroll
// listenToResize = true: run checks on window resize
// listenToMutations = true: run checks when DOM mutations happen

// methods:
// vtOutOfView.init(): gather initial elements and attach listeners
// vtOutOfView.destroy(): remove listeners
// vtOutOfView.scheduleCheck({refreshElements}): debounce checks into one animation frame
// vtOutOfView.refreshElements(): query and update tracked elements using this.selector
// vtOutOfView.setElements(elements): update tracked elements
// vtOutOfView.check(): toggle view-transition-name based on viewport
// vtOutOfView.checkSync({refreshElements}): run check immediately, bypassing rAF (use before pageswap/pagehide)

// changelog:
// 1.0 initial version
// 1.1 add debounced check scheduler and filtered mutation observer
// 1.2 add checkSync for synchronous pre-snapshot checks

export default class VtOutOfView {
  constructor({
    selector = '[data-vt-name]',
    listenToScroll = true,
    listenToResize = true,
    listenToMutations = true,
  } = {}) {
    this.selector = selector;
    this.listenToScroll = listenToScroll;
    this.listenToResize = listenToResize;
    this.listenToMutations = listenToMutations;
    this.elements = [];

    this.initialized = false;
    this.checkQueued = false;
    this.shouldRefreshElements = false;
    this.frameId = 0;
    this.mutationObserver = undefined;

    this.boundScheduleCheck = this.scheduleCheck.bind(this);
    this.boundHandleMutations = this.handleMutations.bind(this);
  }

  init = () => {
    if (this.initialized) return;

    if (this.listenToScroll) {
      window.addEventListener('scroll', this.boundScheduleCheck, {passive: true});
    }

    if (this.listenToResize) {
      window.addEventListener('resize', this.boundScheduleCheck, {passive: true});
    }

    if (this.listenToMutations) {
      this.mutationObserver = new MutationObserver(this.boundHandleMutations);
      this.mutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'data-vt-name'],
        childList: true,
        subtree: true,
      });
    }

    this.initialized = true;
    this.scheduleCheck({refreshElements: true});
  }

  destroy = () => {
    if (!this.initialized) return;

    if (this.listenToScroll) window.removeEventListener('scroll', this.boundScheduleCheck);
    if (this.listenToResize) window.removeEventListener('resize', this.boundScheduleCheck);
    if (this.mutationObserver) this.mutationObserver.disconnect();

    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }

    this.checkQueued = false;
    this.shouldRefreshElements = false;

    this.initialized = false;
  }

  scheduleCheck = ({refreshElements = false} = {}) => {
    if (refreshElements) this.shouldRefreshElements = true;
    if (this.checkQueued) return;

    this.checkQueued = true;
    this.frameId = requestAnimationFrame(() => {
      this.checkQueued = false;
      this.frameId = 0;

      if (this.shouldRefreshElements) {
        this.elements = Array.from(document.querySelectorAll(this.selector));
        this.shouldRefreshElements = false;
      }

      this.check();
    });
  }

  handleMutations = (mutations) => {
    let refreshElements = false;

    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        refreshElements = true;
        break;
      }

      if (mutation.type === 'attributes') {
        const attrName = mutation.attributeName;
        if (attrName === 'data-vt-name') {
          refreshElements = true;
        }

        if (refreshElements) break;
      }
    }

    this.scheduleCheck({refreshElements});
  }

  refreshElements = () => {
    this.scheduleCheck({refreshElements: true});
  }

  checkSync = ({refreshElements = false} = {}) => {
    if (refreshElements) {
      this.elements = Array.from(document.querySelectorAll(this.selector));
      this.shouldRefreshElements = false;
    }
    this.check();
  }

  setElements = (elements = []) => {
    this.elements = Array.from(elements);
    this.scheduleCheck();
  }

  check = () => {
    this.elements.forEach(vtEl => {
      const vtName = vtEl.dataset.vtName;
      if (!vtName) return;

      const rect = vtEl.getBoundingClientRect();
      const isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight || rect.left > window.innerWidth || rect.right < 0;

      if (isOutOfView) {
        if (vtEl.style.getPropertyValue('view-transition-name')) vtEl.style.removeProperty('view-transition-name');
      } else {
        if (!vtEl.style.getPropertyValue('view-transition-name')) vtEl.style.setProperty('view-transition-name', vtName);
      }
    });
  }
}